import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Center,
  Loader,
  Table,
  Text
} from "@mantine/core";
import { IconCheck, IconMinus, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { InventoryStockResults, Medicine } from "@app/services/drugDelivery/types";

interface ConfirmArticlePayload {
  articleId: number;
  batchId: number;
  tradename: string;
  lotNumber: string;
  qty: number;
  cum: string;
  invima: string;
  genericId: number;
  genericName: string;
}

interface InventoryStockViewProps {
  data: InventoryStockResults | null;
  isLoading: boolean;
  selectedMedicine: Medicine | null;
  onConfirmArticle: (payload: ConfirmArticlePayload) => void;
}

export default function InventoryStockView({
  data,
  isLoading,
  selectedMedicine,
  onConfirmArticle
}: InventoryStockViewProps) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  if (isLoading) {
    return (
      <Center py="xl" data-testid="inventorystock-loader">
        <Loader />
      </Center>
    );
  }

  if (data === null) {
    return null;
  }

  const getQty = (batchId: number) => quantities[batchId] ?? 0;

  const increment = (batchId: number, maxQty: number) => {
    setQuantities((prev) => ({
      ...prev,
      [batchId]: Math.min((prev[batchId] ?? 0) + 1, maxQty)
    }));
  };

  const decrement = (batchId: number) => {
    setQuantities((prev) => ({
      ...prev,
      [batchId]: Math.max((prev[batchId] ?? 0) - 1, 0)
    }));
  };

  return (
    <Box data-testid="inventorystock-container">
      <Box mb="sm">
        <Text fw={600} size="md">
          {data.medicine.name}
        </Text>
        <Box className="flex gap-2" mt={4}>
          <Badge variant="light" color="blue">
            CUM: {data.medicine.cum}
          </Badge>
          <Badge variant="light" color="teal">
            INVIMA: {data.medicine.invima}
          </Badge>
        </Box>
      </Box>

      <Accordion data-testid="inventorystock-accordion">
        {data.generics.map((generic) => (
          <Accordion.Item
            key={generic.genericId}
            value={String(generic.genericId)}
            data-testid={`inventorystock-generic-${generic.genericId}`}
          >
            <Accordion.Control>{generic.genericName}</Accordion.Control>
            <Accordion.Panel>
              <Table
                className="biowel-table"
                withTableBorder
                data-testid={`inventorystock-table-${generic.genericId}`}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th bg="#fff">Nombre comercial</Table.Th>
                    <Table.Th bg="#fff">Lote</Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Vencimiento
                    </Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Stock disponible
                    </Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Cant. a entregar
                    </Table.Th>
                    <Table.Th bg="#fff" ta="center">
                      Acciones
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {generic.articles.length ? (
                    generic.articles.map((article) => {
                      const qty = getQty(article.batchId);
                      const expirationDate = (article.expirationDate ?? "")
                        .split("T")[0]
                        .replaceAll("/", "-");
                      const exceedsOrder =
                        selectedMedicine?.qtyOrder !== undefined && qty > selectedMedicine.qtyOrder;

                      return (
                        <Table.Tr
                          key={article.batchId}
                          data-testid={`inventorystock-row-${article.batchId}`}
                        >
                          <Table.Td>{article.tradename}</Table.Td>
                          <Table.Td>{article.lotNumber}</Table.Td>
                          <Table.Td ta="center">{expirationDate}</Table.Td>
                          <Table.Td ta="center">{article.onHand}</Table.Td>
                          <Table.Td ta="center">
                            <Box className="flex items-center justify-center gap-1">
                              <ActionIcon
                                size="xs"
                                variant="light"
                                data-testid={`inventorystock-decrement-${article.batchId}`}
                                disabled={qty === 0}
                                onClick={() => decrement(article.batchId)}
                              >
                                <IconMinus size={12} />
                              </ActionIcon>
                              <Text size="sm" w={24} ta="center">
                                {qty}
                              </Text>
                              <ActionIcon
                                size="xs"
                                variant="light"
                                data-testid={`inventorystock-increment-${article.batchId}`}
                                disabled={qty === article.onHand}
                                onClick={() => increment(article.batchId, article.onHand)}
                              >
                                <IconPlus size={12} />
                              </ActionIcon>
                            </Box>
                            {exceedsOrder && (
                              <Text size="xs" c="orange" mt={2}>
                                La cantidad seleccionada supera la cantidad ordenada
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td ta="center">
                            <ActionIcon
                              color="green"
                              variant="light"
                              data-testid={`inventorystock-confirm-${article.batchId}`}
                              disabled={qty === 0}
                              onClick={() =>
                                onConfirmArticle({
                                  articleId: article.articleId,
                                  batchId: article.batchId,
                                  tradename: article.tradename,
                                  lotNumber: article.lotNumber,
                                  qty,
                                  cum: data.medicine.cum,
                                  invima: data.medicine.invima,
                                  genericId: generic.genericId,
                                  genericName: generic.genericName
                                })
                              }
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    <Table.Tr>
                      <Table.Td ta="center" colSpan={6}>
                        No hay artículos disponibles.
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {data.generics.length === 0 && (
        <Alert color="yellow" mt="sm">
          No se encontraron genéricos para este medicamento.
        </Alert>
      )}
    </Box>
  );
}
